import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Dumbbell, ArrowLeft, TrendingUp, Clock, Users, Repeat, CheckCircle } from 'lucide-react';

const FitnessCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/industries" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Industries
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center">
                <Dumbbell className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Fitness & Wellness
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  FlowState Yoga Collective
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How a yoga studio chain improved membership retention by 45% through pricing structure optimization
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1200"
              alt="FlowState Yoga Collective Studio"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">6</div>
              <div className="text-[#94A3B8]">Studio Locations</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Repeat className="w-8 h-8 text-green-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">2,400</div>
              <div className="text-[#94A3B8]">Active Members</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-pink-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">8 years</div>
              <div className="text-[#94A3B8]">Building Community</div>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                FlowState Yoga Collective had grown from one studio to six locations over 8 years, but their pricing hadn't evolved with them. They offered unlimited memberships, class packs (10 and 20 classes), and drop-in rates—but had no data on which was actually best for the business.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                The problem became clear when they analyzed their numbers: class packs were cannibalizing unlimited memberships. Members were buying 20-class packs instead of committing to monthly memberships, leading to unpredictable revenue and higher churn.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                Even worse, their promotions were attracting deal-seekers who churned after the first month. They needed a pricing structure that rewarded commitment without scaring away newcomers.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                PriceStrike helped FlowState redesign their entire membership structure:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Tiered Membership Analysis:</span>
                    <span className="text-[#94A3B8]"> Modeled different tier structures to find the optimal balance between accessibility and commitment.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Class Pack Repositioning:</span>
                    <span className="text-[#94A3B8]"> Adjusted class pack pricing to make memberships the clear value choice for regular practitioners.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Churn Prediction:</span>
                    <span className="text-[#94A3B8]"> Identified pricing friction points that led to cancellations and implemented proactive retention offers.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Promotion ROI Tracking:</span>
                    <span className="text-[#94A3B8]"> Replaced blanket discounts with targeted offers that attracted members more likely to stay long-term.</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-[#111827] border border-green-500/30 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">45%</div>
                  <div className="text-[#94A3B8]">Improvement in membership retention</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">32%</div>
                  <div className="text-[#94A3B8]">Increase in monthly recurring revenue</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">60%</div>
                  <div className="text-[#94A3B8]">More class pack to membership conversions</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">3x</div>
                  <div className="text-[#94A3B8]">Better promotion ROI</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-2xl p-8 mb-12">
              <blockquote className="text-xl text-[#F8FAFC] italic mb-4">
                "We were losing members to cheaper options. Turns out our pricing structure was the problem, not our prices. Now our class packs feed into memberships instead of competing with them."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">MK</span>
                </div>
                <div>
                  <div className="text-[#F8FAFC] font-semibold">Maya Krishnan</div>
                  <div className="text-[#94A3B8]">Founder, FlowState Yoga Collective</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/coming-soon">
              <button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Start Your Free Trial
              </button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default FitnessCaseStudy;
